import { Bug, ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import { useOptionalTheme } from '@/contexts/ThemeProvider';
import { useUserProfile } from '@/hooks/account/useUser';
import { useSubscription } from '@/hooks/account/useBilling';
import { SETTINGS_SECTIONS, type SettingsSection } from './SettingsNav';

/**
 * Settings on a phone, as two screens - the pattern iOS Settings, Linear and
 * Notion use on a phone, and the one a phone user already knows:
 *
 * - `SettingsHome` - who you are, then one 52px row per section, Report a
 *   problem on its own, and Sign out last and alone.
 * - `SettingsSectionScreen` - "‹ Settings" and the section, full width,
 *   scrolling with the page.
 *
 * They replace a chip row that showed three of its seven items (the rest -
 * Learning style, Appearance, Report and Sign out - scrolled off unseen, Sign
 * out among the sections), above a card that repeated the section's title and
 * nested the content three boxes deep. `SettingsLayout` picks these below
 * `md`, and the two-panel layout from `md` up.
 */

const ROW =
  'flex min-h-[52px] w-full items-center gap-3 px-4 text-left text-[15px] text-foreground transition-colors active:bg-muted';

/** The plan, in the words the Payment section uses. */
function planLabel(tier: string | undefined): string | undefined {
  switch (tier?.toLowerCase()) {
    case 'free':
      return 'Free';
    case 'paid':
    case 'plus':
    case 'pro':
    case 'comped':
      return 'Plus';
    default:
      return undefined;
  }
}

const MODE_LABEL = { light: 'Light', dark: 'Dark', system: 'System' };

function initialsOf(name: string | null | undefined, email: string) {
  return ((name && name.trim()) || email)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function SettingsHome({
  onOpen,
  onSignOut,
  isSigningOut,
}: {
  onOpen: (section: SettingsSection) => void;
  onSignOut: () => void;
  isSigningOut: boolean;
}) {
  const { data: profile } = useUserProfile();
  const { data: subscription } = useSubscription();
  const theme = useOptionalTheme()?.theme;
  const hints: Partial<Record<SettingsSection, string>> = {
    payment: planLabel(subscription?.tier),
    appearance: theme ? MODE_LABEL[theme.mode] : undefined,
  };

  return (
    <div className='h-full overflow-y-auto pb-8'>
      <h1 className='px-1 pb-4 pt-6 text-title text-foreground'>Settings</h1>

      {profile && (
        <Group label='Your profile'>
          <button
            type='button'
            onClick={() => onOpen('personal')}
            className={cn(ROW, 'min-h-[72px] py-3')}
          >
            <span
              aria-hidden='true'
              className='flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-foreground text-sm font-semibold text-background'
            >
              {profile.avatar_url ? (
                <img
                  src={`${profile.avatar_url}?v=${encodeURIComponent(profile.updated_at)}`}
                  alt=''
                  className='h-full w-full object-cover'
                />
              ) : (
                initialsOf(profile.full_name, profile.email)
              )}
            </span>
            <span className='min-w-0 flex-1'>
              <span className='block truncate font-medium'>
                {profile.full_name || 'Your account'}
              </span>
              <span className='block truncate text-sm text-muted-foreground'>
                {profile.email}
              </span>
            </span>
            <ChevronRight
              className='h-4 w-4 flex-shrink-0 text-muted-foreground'
              aria-hidden='true'
            />
          </button>
        </Group>
      )}

      <Group label='Settings sections' nav>
        {SETTINGS_SECTIONS.map(({ key, label, icon: Icon }) => (
          <SectionRow
            key={key}
            label={label}
            icon={Icon}
            hint={hints[key]}
            onClick={() => onOpen(key)}
          />
        ))}
      </Group>

      <Group label='Help'>
        <SectionRow
          label='Report a problem'
          icon={Bug}
          onClick={() => onOpen('report')}
        />
      </Group>

      <Group label='Session'>
        <button
          type='button'
          onClick={onSignOut}
          disabled={isSigningOut}
          className={cn(
            ROW,
            'justify-center font-medium text-red-800 disabled:opacity-60 dark:text-red-300'
          )}
        >
          <LogOut className='h-4 w-4' aria-hidden='true' />
          {isSigningOut ? 'Signing out…' : 'Sign out'}
        </button>
      </Group>
    </div>
  );
}

// `relative`: the page lattice is a positioned layer and paints over any
// background that is not.
const GROUP =
  'relative mb-4 divide-y divide-border/70 overflow-hidden rounded-xl border border-border/60 bg-card';

/**
 * A rounded group of rows, divided by hairlines. The sections group is the
 * page's "Settings sections" nav - the same landmark as the desktop side
 * panel; the others are labelled groups.
 */
function Group({
  label,
  nav = false,
  children,
}: {
  label: string;
  nav?: boolean;
  children: React.ReactNode;
}) {
  return nav ? (
    <nav aria-label={label} className={GROUP}>
      {children}
    </nav>
  ) : (
    <div role='group' aria-label={label} className={GROUP}>
      {children}
    </div>
  );
}

function SectionRow({
  label,
  icon: Icon,
  hint,
  onClick,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <button type='button' onClick={onClick} className={ROW}>
      <Icon className='h-5 w-5 flex-shrink-0 text-muted-foreground' />
      <span className='flex-1'>{label}</span>
      {hint && <span className='text-sm text-muted-foreground'>{hint}</span>}
      <ChevronRight
        className='h-4 w-4 flex-shrink-0 text-muted-foreground'
        aria-hidden='true'
      />
    </button>
  );
}

/**
 * One section, full screen. Keeps the desktop panel's heading order - an
 * `h2` section title over sections that open at `h3` - under a visually
 * hidden `h1`, so the page still has one top-level heading.
 */
export function SettingsSectionScreen({
  title,
  subtitle,
  onBack,
  children,
}: {
  title: string;
  subtitle: string;
  onBack: () => void;
  children: React.ReactNode;
}) {
  return (
    // Full-bleed paper (`-mx-4` undoes the page gutter): beside `md` the
    // section sits on a painted panel, and without one the page lattice ran
    // through every card's text. `relative` so it paints over the lattice.
    <div className='relative -mx-4 flex h-full flex-col bg-card px-4'>
      <div className='relative flex-shrink-0 border-b border-border/60 pb-3 pt-2'>
        {/* Inside the padded header, so it sits where the visible page
            starts - not on the surface's top edge, under a banner's. */}
        <h1 className='sr-only'>Settings</h1>
        <button
          type='button'
          onClick={onBack}
          className='-ml-2 flex min-h-11 items-center gap-0.5 rounded-md px-2 text-sm font-medium text-muted-foreground transition-colors active:bg-muted'
        >
          <ChevronLeft className='h-5 w-5' aria-hidden='true' />
          Settings
        </button>
        <h2 className='mt-1 text-heading text-foreground'>{title}</h2>
        <p className='mt-1 text-sm text-muted-foreground'>{subtitle}</p>
      </div>
      <div className='min-h-0 flex-1 overflow-y-auto pb-10 pt-5'>
        {children}
      </div>
    </div>
  );
}
